import './fileUpload.css';
import React, { useState, useEffect, useCallback } from 'react';
import { FileUploader } from 'react-drag-drop-files';
import moment from 'moment';
import { Box, IconButton, Typography, Button, Select, MenuItem, FormControl, Tooltip } from '@mui/material';
import { CloudUpload, Delete, Description, InfoOutlined } from '@mui/icons-material';
import { useAppSelector } from '../../app/hooks';
import User from '../../interfaces/users.interface';
import { DataCell, DataRow } from '../Tables/DataList';
import StandardDialog from '../Modals/StandardDialog';
import FileViewer from '../FileViewer/FileViewer';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { useTheme } from '@mui/material/styles';
import { USER_TYPES } from '../../services/user';

export interface FileUpload {
  id?: number;
  file: File | string;
  date: string;
  user: User
  isPublic: boolean | '';
}

export default function FileUploaderComp({
  showCOPublicModal,
  isCertUpload = false,
  fileList,
  callback,
  multiple,
  hideFileList,
  dontShowUpload,
  boxHeight,
  enablePublic,
  assessorVisible = false,
  uploadType = 'file',
  helperText,
  showProTip = false,
  disabled = false,
} :{
  showCOPublicModal?: boolean,
  isCertUpload?: boolean,
  fileList?:FileUpload[],
  callback?: any,
  multiple?: boolean,
  disabled?: boolean;
  hideFileList?: boolean,
  dontShowUpload?: boolean,
  boxHeight?: number,
  enablePublic?: boolean,
  assessorVisible?: boolean,
  uploadType?: string
  helperText?: string;
  showProTip?: boolean;
}) {
  const theme = useTheme();
  const { user } = useAppSelector(state => state.user);
  const { whiteListedFileTypes } = useAppSelector(state => state.fileSlice);
  const canSeeIsPublic = user?.type === USER_TYPES.TPN_ADMIN || (user?.type === USER_TYPES.VENDOR_ADMIN && enablePublic);
  const [files, setFiles] = useState(fileList ? fileList : []);
  const [showPublic, setShowPublic] = useState(false);
  const [showInvalidFileType, setShowInvalidFileType] = useState(false);
  const handleSetFiles = useCallback((nfiles:FileList | File) => {
    if (!user) return;
    if ((nfiles as FileList).length) {
      const setFileList:FileUpload[] = Array.from(nfiles as FileList).map(file => { return ({ file, user, date: moment().utc().format('YYYY-MM-DDTHH:mm:ssZZ'), isPublic: '' }); });
      setFiles([ ...files, ...setFileList ]);
      if (callback) callback([ ...files, ...setFileList ]);
    } else {
      const setFileList:FileUpload[] = [{ file:(nfiles as File), user, date: moment().utc().format('YYYY-MM-DDTHH:mm:ssZZ'), isPublic: '' }];
      if (files.length === 0) {
        setFiles(setFileList);
        if (callback) callback(setFileList);
      } else {
        setFiles([ ...files, ...setFileList ]);
        if (callback) callback([ ...files, ...setFileList ]);
      }
    }
  }, [files]);

  const handleIsPublicChange = (value: string | boolean, index: number) => {
    if (!(user?.type === USER_TYPES.TPN_ADMIN || user?.type === USER_TYPES.VENDOR_ADMIN)) {
      return;
    }

    const newFiles = files.map((file, fileIdx) => {
      if (index === fileIdx) {
        file.isPublic = value === 'true' ? true : false;
      }
      return file;
    });

    setFiles(newFiles);
    if (callback) callback(newFiles);
  };

  const isFileImage = (file:File) => {
    return file && file?.type?.split('/')[0] === 'image';
  };

  const isUrlImage = ( url:string ) => {
    const extension = url.split(/[#?]/)[0].split('.').pop()?.trim();
    if (!extension) return;
    return ['jpg', 'gif', 'png', 'jpeg', 'svg', 'webp'].indexOf(extension) > -1;
  };

  const getFileNamefromURL = ( url:string ) => {
    const filename = url.split(/[#?]/)[0].split('/').pop()?.trim();
    return filename;
  };

  useEffect(()=> {
    if (fileList) setFiles(fileList);
  }, [fileList]);

  const deleteFile = (index: number) => {
    const setFileList:FileUpload[] = files.filter((file, i) => i !== index);
    setFiles(files.filter((file, i) => i !== index));
    if (callback) callback(setFileList);
  };

  const isValidFileType = useCallback((name: string) => {
    const extension = name.split('.').pop()?.split('?')[0].toLowerCase();
    return extension && whiteListedFileTypes ? whiteListedFileTypes.includes(extension) : false;
  }, [whiteListedFileTypes]);

  const handleSetFilePreload = React.useCallback(async (nFiles: FileList | File) => {
    const filesArray = nFiles instanceof FileList ? Array.from(nFiles) : [nFiles];
    const hasWhiteListFileExtensions = filesArray.every(file => {
      return isValidFileType(file.name);
    });

    if (!hasWhiteListFileExtensions) {
      setShowInvalidFileType(true);
      return;
    }

    if (!filesArray.length) return;
    setShowInvalidFileType(false);
    if (showCOPublicModal) setShowPublic(true);
    handleSetFiles(nFiles);
  }, [handleSetFiles, showCOPublicModal]);

  const handleSaveFileIsPublicInitValue = useCallback((isPublic:boolean) => {
    if (files) {
      const newFiles = files.map(file => {
        file.isPublic = file.isPublic === '' ? isPublic : file.isPublic;
        return file;
      });
      setFiles(newFiles);
      if (callback) callback(newFiles);
    }
    setShowPublic(false);
  }, [files]);

  const cellStyle = { fontSize: '15px' };
  const [showFileViewer, setShowFileViewer] = useState(false);
  const [previewFile, setPreviewFile] = useState('');
  const setFileForPreview = (file: string) => () => {
    setPreviewFile(file);
    setShowFileViewer(true);
  };

  const handleCloseFileViewer = () => {
    setPreviewFile('');
    setShowFileViewer(false);
  };

  return (
    <>
    <StandardDialog
      title={'Visible to Content Owners?'}
      subtitle={`Select 'Yes' to allow Content Owners to view this ${uploadType}.${assessorVisible ? ' Otherwise, it will only be accessible to your Assessor.' : ''}`}
      handleClose={() => setShowPublic(false)}
      maxWidth='sm'
      isOpen={showPublic}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          gap: '10px',
          width:'100%',
        }}>
        <Button
             onClick={() => handleSaveFileIsPublicInitValue(false)}
            color="primary"
            variant="outlined"
          >
            No
          </Button>
            <Button
              color="primary"
              variant="contained"
              type="submit"
              onClick={() => handleSaveFileIsPublicInitValue(true)}
            >
              Yes
            </Button>
          </Box>
    </StandardDialog>
    <Box>
      {!dontShowUpload && <Box>
        <Box sx={{
          display: 'flex',
          alignItems: 'flex-start',
        }}>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 500 }}>
            { isCertUpload ? 'Certification' : 'Evidence Upload' }
          </Typography>
          {helperText && (
            <Tooltip title={helperText} arrow placement="right">
              <IconButton size="small" aria-label="info">
                <InfoOutlined fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
        { showProTip && (
        <Box
          sx={{
            backgroundColor: '#E8F4FD',
            borderLeft: `4px solid ${theme.palette.info.main}`,
            padding: 1.5,
            borderRadius: 1,
            mb: 2,
            display: 'flex',
            alignItems: 'flex-start',
            gap: 1,
          }}
        >
          <Typography variant="body2" sx={{ fontSize: 14, color: theme.palette.text.secondary }}>
            <strong>Pro Tip:</strong> Upload evidence for faster remediation validation.
          </Typography>
        </Box>
        )}
        <Typography variant="body2" color="text.secondary" mb={1}>
          Accepted file types: <strong>JPG, PNG, JPEG, GIF, PDF, DOCX, XLSX</strong>. Max size: <strong>50MB</strong>.
        </Typography>
        <FileUploader
          disabled={disabled}
          multiple={multiple !== undefined ? multiple : true}
          handleChange={(nfiles:File | FileList) => handleSetFilePreload(nfiles)}
          classes="dropZoneFull"
          hoverTitle="."
        >
          <Box
            sx={{
              width: '100%',
              height: boxHeight ? `${boxHeight}px` : '300px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: `1px dashed ${theme.palette.customThemeColors?.lightGray}`,
              position: 'relative',
            }}
          >
            <Box
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '0.5px',
                justifyContent: 'center',
              }}
              >
              <CloudUpload
                sx={(muiTheme) => ({
                  fontSize: 48,
                  color: muiTheme.palette.customThemeColors?.lightGray || '#ccc',
                })}
              />
              <Typography variant="body1" sx={{}}>
                Drag & Drop to Upload Files
              </Typography>
              <Typography variant="body2" color="text.secondary">
                OR
              </Typography>
              <Button
                variant="contained"
                component="label"
                sx={{ textTransform: 'none' }}
                disabled={disabled}
              >
                Browse
                <input
                  type="file"
                  hidden
                  multiple={multiple ?? true}
                  onChange={e => handleSetFilePreload(e.target.files!)}
                />
              </Button>
            </Box>
            {showInvalidFileType && (
              <Box
                sx={{
                  position: 'absolute',
                  bottom: boxHeight ? `${(boxHeight / 2) - 41}px` : '60px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  textAlign: 'center',
                }}
              >
                <WarningAmberIcon sx={{ color: '#B55C5a', mr: 1, fontSize: 20 }} />
                <Typography variant="body1">Invalid file type.</Typography>
              </Box>
            )}          
          </Box>
        </FileUploader>
      </Box>}
      {!hideFileList && files.length > 0 &&
        <Box>
          <DataRow header>
            <DataCell sx={cellStyle} xs={2}>Preview</DataCell>
            <DataCell sx={cellStyle} xs={canSeeIsPublic ? 3 : 4}>Name</DataCell>
            <DataCell sx={cellStyle} xs={2}>User</DataCell>
            <DataCell sx={cellStyle} xs={canSeeIsPublic ? 2 : 3}>Uploaded</DataCell>
            {canSeeIsPublic &&
            <DataCell sx={cellStyle} xs={2}>CO Visibility</DataCell>
            }
            <DataCell xs={1}>-</DataCell>
          </DataRow>
          {files.map((file, index) => (
            <DataRow key={index}>
             {typeof file?.file !== 'string' && <>
                 <DataCell xs={2} sx={{ textAlign: 'center' }}>{isFileImage(file?.file) ? <img src={URL.createObjectURL(file.file)} style={{ maxWidth: '100%' }} /> : <Description fontSize='large' />}</DataCell>
                 <DataCell xs={canSeeIsPublic ? 3 : 4}><Typography sx={{ maxWidth: '100%', overflowWrap: 'break-word' }}>{file?.file?.name}</Typography></DataCell>
              </>}
              {typeof file.file === 'string' && <>
                <DataCell xs={2} sx={{ textAlign: 'center' }}>
                {  isValidFileType(file.file) ? (
                  <div onClick={setFileForPreview(file.file)}>{isUrlImage(file.file) ? <img src={file.file} style={{ maxWidth: '100%' }} /> : <Description fontSize='large' />}</div>
                ) : (
                  <a href={file.file} style={{ color: 'black' }} target="_blank" rel="noreferrer">{isUrlImage(file.file) ? <img src={file.file} style={{ maxWidth: '100%' }} /> : <Description fontSize='large' />}</a>
                )}
                </DataCell>
                <DataCell xs={canSeeIsPublic ? 3 : 4}><Typography sx={{ maxWidth: '100%', overflowWrap: 'break-word' }}>{getFileNamefromURL(file.file)}</Typography></DataCell>
              </>}
              <DataCell xs={2}>{file.user?.firstName} {file.user?.lastName}</DataCell>
              <DataCell xs={canSeeIsPublic ? 2 : 3}>{moment(file.date).format('MM/DD/YYYY hh:mm')}</DataCell>
              {canSeeIsPublic && <DataCell xs={2} sx={{ textAlign: 'center' }}>
                <FormControl error={file.isPublic === ''} fullWidth>
                  <Select
                    onChange={(e) => handleIsPublicChange(e.target.value, index)}
                    disabled={user?.type === USER_TYPES.TPN_ADMIN}
                    value={file.isPublic}
                  >
                    <MenuItem value='true'>Yes</MenuItem>
                    <MenuItem value='false'>No</MenuItem>
                  </Select>
                </FormControl>
              </DataCell>}
              <DataCell xs={1}>{!dontShowUpload && <IconButton onClick={() => deleteFile(index)}><Delete /></IconButton>}</DataCell>
            </DataRow>
          ))}
        </Box>
      }
    </Box>
    <FileViewer previewFile={previewFile} showFileViewer={showFileViewer} closeFileViewer={handleCloseFileViewer}/>
    </>
  );
}
